import {
  ACCOUNT_PROVIDERS,
  HubCurrencies,
  MerkPosition,
} from "@letsbit/malcolmlb";
import { FormattedMessage } from "react-intl";
import { messages } from "./messages";
import { useMemo } from "react";
import { useFormatter } from "hooks/utils/useFormatter";
import { ReactComponent as WarningIcon } from "assets/images/icons/warning.svg";
import { BadgeDirection, CardContainer } from "@letsbit/milkylb";
import { useNavigate } from "react-router-dom";
import CustomFormattedMessage from "components/CustomFormattedMessage";
import { MarketPositionsProps } from "./types";
import { useCurrencies } from "hooks/user/useCurrencies";
import { useChartPositionsGroups } from "hooks/user/useChartPositionsGroups";
import { Balance } from "components/Balance";

export const MarketPositions: React.FC<MarketPositionsProps> = ({
  positions,
  onClickPosition,
  provider = ACCOUNT_PROVIDERS.ESCO,
}) => {
  const { formatValue } = useFormatter();
  const navigate = useNavigate();
  const { data: currencies } = useCurrencies();

  const {
    totalVolume,
    totalVolumeWithoutNegatives,
    positionCategories,
    currenciesObj,
    sortedPositions,
  } = useMemo(() => {
    if (!positions) return { totalVolume: "" };
    const totalVolume = positions.reduce((acc, position) => {
      return acc + +position.market_value;
    }, 0);
    const totalVolumeWithoutNegatives = positions.reduce((acc, position) => {
      return acc + Math.max(+position.market_value, 0);
    }, 0);

    const currenciesObj = currencies?.reduce(
      (acc, currency) => {
        acc[currency.id] = currency;
        return acc;
      },
      {} as Record<string, HubCurrencies>,
    );

    const positionCategories: Record<string, MerkPosition[]> = positions.reduce(
      (acc, position) => {
        if (position.type in acc) {
          acc[position.type].push(position);
        } else {
          acc[position.type] = [position];
        }
        return acc;
      },
      {} as Record<string, MerkPosition[]>,
    );

    const sortedPositions = positions
      ?.filter((position) => +position.market_value > 0)
      .sort((a, b) => +b.market_value - +a.market_value);

    return {
      totalVolume,
      totalVolumeWithoutNegatives,
      positionCategories,
      currenciesObj,
      sortedPositions,
    };
  }, [positions]);

  const { computeGroupsByAsset, computeGroupsByCategory } =
    useChartPositionsGroups();

  const { groups: positionGroups } =
    provider === ACCOUNT_PROVIDERS.ESCO
      ? computeGroupsByCategory(sortedPositions)
      : computeGroupsByAsset(sortedPositions);

  return (
    <div className="-m-2 w-full overflow-y-auto p-2">
      <CardContainer className="mb-4 w-auto p-4">
        <div className="flex flex-row items-center gap-x-4 pb-4 pt-4">
          <Balance balanceGroups={positionGroups} />
          <div className="flex flex-col gap-2 pr-4">
            <h3 className="font-['Tomato_Grotesk'] text-2xl font-bold text-[--milkylb-color-neutral-600]">
              <FormattedMessage {...messages.totalVolume} />
            </h3>
            <h2 className="font-['Tomato_Grotesk'] text-4xl font-bold text-[--milkylb-color-neutral-800]">
              $ {formatValue(totalVolume.toString())}{" "}
              {positionCategories &&
              Object.entries(positionCategories).length > 0
                ? currenciesObj?.[
                    Object.values(positionCategories)[0][0].price_currency
                  ]?.ticker_id.toUpperCase()
                : "ARS"}
            </h2>
          </div>
          {provider === ACCOUNT_PROVIDERS.ESCO && (
            <div className="flex items-center text-xs font-semibold text-[--milkylb-color-neutral-400]">
              <WarningIcon className="shrink-0" />{" "}
              <p className="ml-2">
                <FormattedMessage
                  {...messages.warningText}
                  values={{ br: <br /> }}
                />
              </p>
            </div>
          )}
        </div>
      </CardContainer>
      <div className="w-full overflow-hidden">
        {positionCategories && Object.entries(positionCategories).length > 0 ? (
          Object.entries(positionCategories).map(([category, positions]) => {
            const totalCategory = positions.reduce((acc, position) => {
              return acc + +position.market_value;
            }, 0);
            const totalCategoryWithoutNegatives = positions.reduce(
              (acc, position) => {
                return acc + Math.max(+position.market_value, 0);
              },
              0,
            );

            return (
              <div
                key={category}
                className="mb-8 flex flex-col rounded-2xl bg-[--milkylb-color-neutral-100] p-6"
              >
                <div className="mb-2 grid grid-cols-5">
                  <h2 className="col-span-3 grid-cols-subgrid text-xl font-bold text-[--milkylb-color-neutral-600]">
                    {category === "MON" ? (
                      <FormattedMessage {...messages.currencies} />
                    ) : (
                      category
                    )}
                  </h2>
                  <div className="flex flex-col">
                    <label className="text-sm text-[--milkylb-color-neutral-400]">
                      <FormattedMessage {...messages.valuationLabel} />
                    </label>
                    <label className="text-lg font-bold text-[--milkylb-color-neutral-600]">
                      $ {formatValue(totalCategory.toString())}{" "}
                      {currenciesObj?.[
                        positions[0].price_currency
                      ]?.ticker_id.toUpperCase()}
                    </label>
                  </div>
                  <div className="flex flex-col">
                    <label className="text-sm text-[--milkylb-color-neutral-400]">
                      <FormattedMessage {...messages.percentageLabel} />
                    </label>
                    <label className="text-lg font-bold text-[--milkylb-color-neutral-600]">
                      {formatValue(
                        (
                          (totalCategoryWithoutNegatives /
                            totalVolumeWithoutNegatives) *
                          100
                        ).toString(),
                      )}
                      %
                    </label>
                  </div>
                </div>
                <div className="grid grid-cols-5 gap-x-1 pl-4">
                  <label className="text-sm text-[--milkylb-color-neutral-400]">
                    <FormattedMessage {...messages.assetLabel} />
                  </label>
                  <label className="text-sm text-[--milkylb-color-neutral-400]">
                    <FormattedMessage {...messages.amountLabel} />
                  </label>
                  <label className="text-sm text-[--milkylb-color-neutral-400]">
                    <FormattedMessage {...messages.priceLabel} />
                  </label>
                  <label className="text-sm text-[--milkylb-color-neutral-400]">
                    <FormattedMessage {...messages.valuationLabel} />
                  </label>

                  <label className="text-sm text-[--milkylb-color-neutral-400]">
                    <FormattedMessage {...messages.percentageLabel} />
                  </label>
                </div>
                {positions.map((position) => (
                  <CardContainer
                    className="mt-2 min-h-[48px] cursor-pointer"
                    onClick={
                      onClickPosition
                        ? () => onClickPosition(position)
                        : () =>
                            navigate(
                              `/logged/byma/positions/${position.symbol}`,
                            )
                    }
                  >
                    <div className="grid grid-cols-5 gap-x-1 p-2 pl-4">
                      <div className="flex flex-row items-center">
                        <div className="m-1 mr-2 w-[32px] flex-shrink-0">
                          <BadgeDirection
                            img={position.asset.icon_url}
                            size={32}
                          />
                        </div>
                        <div className="flex flex-col overflow-hidden">
                          <p className="truncate text-lg/5 font-bold text-[--milkylb-color-neutral-600]">
                            {position.asset.name}
                          </p>
                        </div>
                      </div>
                      <div className="flex h-full items-center">
                        <p className="text-lg font-bold text-[--milkylb-color-neutral-600]">
                          {formatValue(position.qty)}
                        </p>
                      </div>
                      <div className="flex h-full items-center">
                        <p className="text-lg font-bold text-[--milkylb-color-neutral-600]">
                          $ {formatValue(position.current_price.toString())}{" "}
                          {currenciesObj?.[
                            position.price_currency
                          ]?.ticker_id.toUpperCase()}
                        </p>
                      </div>
                      <div className="flex h-full items-center">
                        <p className="text-lg font-bold text-[--milkylb-color-neutral-600]">
                          $ {formatValue((+position.market_value).toString())}{" "}
                          {currenciesObj?.[
                            position.price_currency
                          ]?.ticker_id.toUpperCase()}
                        </p>
                      </div>
                      <div className="flex h-full items-center">
                        <p className="text-lg font-bold text-[--milkylb-color-neutral-600]">
                          {formatValue(
                            (
                              (Math.max(+position.market_value, 0) /
                                totalVolumeWithoutNegatives) *
                              100
                            ).toString(),
                          )}
                          %
                        </p>
                      </div>
                    </div>
                  </CardContainer>
                ))}
              </div>
            );
          })
        ) : (
          <div className="mb-8 flex flex-col rounded-2xl bg-[--milkylb-color-neutral-100] p-6">
            <div className="flex h-full w-full items-center justify-center">
              <p className="text-sm text-[--milkylb-color-neutral-600]">
                <CustomFormattedMessage {...messages.emptyStatePositions} />
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
